import { useState, useEffect, useRef } from 'react'
export const METHODS = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
  PUT: 'PUT',
}

export const errorHandler = (error) => {
  if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
    return null
  }
  if (error.response) {
    return {
      status: error.response.status,
      message: error.response.data?.message || 'Server responded with an error.',
    }
  }
  if (error.request) {
    return {
      status: 421,
      message: 'No response received from server.',
    }
  }
  return {
    status: 1,
    message: error.message || 'Unexpected error occurred.',
  }
}

const useFetch = (config, skip) => {
  const {
    axiosInstance,
    method = METHODS.GET,
    path,
    requestConfig = {},
    dependencies = [],
  } = config

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [reFetch, setReFetch] = useState(0)
  const isMounted = useRef(true)

  const handleReFetch = () => setReFetch((prev) => prev + 1)
  const reset = () => {
    setData(null)
    setError(null)
  }

  // Deep compare requestConfig using stringified value
  const stringifiedRequestConfig = JSON.stringify(requestConfig);

  useEffect(() => {
    isMounted.current = true
    if (skip) return

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        const response = await axiosInstance[method.toLowerCase()](path, {
          ...requestConfig, // Use the original object here
          signal: controller.signal,
        })
        if (isMounted.current) setData(response.data)
      } catch (error) {
        if (isMounted.current) {
          setError(errorHandler(error))
          setData(null)
        }
      } finally {
        if (isMounted.current) setLoading(false)
      }
    }

    fetchData()

    return () => {
      isMounted.current = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosInstance, method, path, reFetch, skip, stringifiedRequestConfig, ...dependencies])

  return { loading, error, data, refetch: handleReFetch, reset }
}

export default useFetch
